import { handleFlexWidthVariable } from './resize-separator/ResizeSeparator.sty.js';
import {
  clamp,
  resolveWidthPx,
  toCssLength,
  type PanelWidth,
} from './width-utils.js';

/** Width of the handle track wherever the CSS variable is not declared. */
const FALLBACK_HANDLE_WIDTH = 8;

/**
 * Flex space the resize handle track takes from the panel and the content.
 * Under visual refresh the handle cancels its own width with a negative
 * margin, so the variable reports 0 there. Reading it from the document
 * element is synchronous and works before the handle element mounts, so the
 * first render already sizes against the right value.
 *
 * @internal
 */
export function resolveHandleWidth(resizable: boolean): number {
  if (!resizable) {
    return 0;
  }
  if (typeof document === 'undefined') {
    return FALLBACK_HANDLE_WIDTH;
  }

  const declared = getComputedStyle(document.documentElement)
    .getPropertyValue(handleFlexWidthVariable)
    .trim();
  const parsed = Number.parseFloat(declared);

  return Number.isNaN(parsed) ? FALLBACK_HANDLE_WIDTH : parsed;
}

/**
 * Everything a panel and its sibling content are allowed to be. The handle
 * track is deliberately absent: it is resolved by `resolvePanelSizing`, so no
 * caller can size against a handle width of its own.
 *
 * @internal
 */
export interface PanelSizingConstraints {
  minWidth: PanelWidth;
  maxWidth: PanelWidth;
  /** Width the sibling content keeps for itself. */
  reservedMin: PanelWidth;
  /** A panel without a resize handle has no handle track to pay for. */
  resizable: boolean;
}

/**
 * The width a panel asks for, in order of precedence. All three are optional
 * because a drag asks for a bare pixel value.
 *
 * @internal
 */
export interface PanelSizingRequest {
  /** Width the consumer controls. */
  controlledWidth?: PanelWidth;
  /** Width the user last dragged to, in px. */
  draggedWidth?: number;
  /** Declared width, used until the panel is controlled or dragged. */
  defaultWidth?: PanelWidth;
}

/** @internal */
export interface PanelSizingValues {
  /** Resolved width in px. Also what the separator reports as its value. */
  width: number;
  widthCss: string;
  /** Smallest width the panel may take, in px. */
  minPx: number;
  /** Largest width the panel may take, in px. Never below `minPx`. */
  maxPx: number;
}

/** @internal */
export interface PanelSizing {
  panel: PanelSizingValues;
  /**
   * The sibling content is only painted. Its `aria-controls` names the panel,
   * so a resize separator reports the panel's bounds, never the content's.
   */
  content: { widthCss: string };
}

interface DeclaredPanelBounds {
  minWidth: PanelWidth;
  maxWidth: PanelWidth;
  /** Width the sibling content keeps for itself. */
  reservedMin: PanelWidth;
}

interface ResolvedPanelBounds {
  /** Declared minimum, in px. */
  minPx: number;
  /**
   * What the panel may actually take: the declared maximum, or less when the
   * container cannot fit it beside the content reserve and the handle track.
   */
  maxPx: number;
}

/**
 * Resolves declared widths against the measured container. A percentage cannot
 * resolve before the first measurement, so an unmeasured container leaves the
 * panel unconstrained rather than collapsing it.
 */
function resolvePanelBounds(
  containerWidth: number,
  declared: DeclaredPanelBounds,
  handleWidth: number,
): ResolvedPanelBounds {
  const minPx = resolveWidthPx(declared.minWidth, containerWidth, 0);
  const declaredMaxPx = resolveWidthPx(
    declared.maxWidth,
    containerWidth,
    Infinity,
  );
  const reservedMinPx = resolveWidthPx(declared.reservedMin, containerWidth, 0);

  let containerMax = Infinity;
  if (containerWidth !== 0) {
    containerMax = containerWidth - reservedMinPx - handleWidth;
  }

  // The minimum wins over a container that cannot fit it, so the maximum never
  // ends up below the minimum and the separator cannot report an inverted range.
  return {
    minPx,
    maxPx: Math.max(minPx, Math.min(declaredMaxPx, containerMax)),
  };
}

function resolveRequestedWidth(
  request: PanelSizingRequest,
  containerWidth: number,
): number {
  const { controlledWidth, draggedWidth, defaultWidth } = request;

  if (controlledWidth !== undefined) {
    return resolveWidthPx(controlledWidth, containerWidth, 0);
  }
  if (draggedWidth !== undefined) {
    return draggedWidth;
  }
  if (defaultWidth !== undefined) {
    return resolveWidthPx(defaultWidth, containerWidth, 0);
  }

  return 0;
}

/**
 * Resolves the geometry of one panel and its sibling content: the width the
 * panel gets, the CSS both columns are painted with, and the bounds the resize
 * separator reports for the panel it controls.
 *
 * This is the only place the handle track width, the requested width and the
 * clamp are composed. Callers own the declared inputs and the drag gesture,
 * never the arithmetic, so a width cannot be clamped twice against two
 * different handle tracks (APPDEV-19606).
 *
 * @internal
 */
export function resolvePanelSizing(
  request: PanelSizingRequest,
  containerWidth: number,
  constraints: PanelSizingConstraints,
): PanelSizing {
  const handleWidth = resolveHandleWidth(constraints.resizable);
  const bounds = resolvePanelBounds(containerWidth, constraints, handleWidth);
  const panelWidth = clamp(
    bounds.minPx,
    resolveRequestedWidth(request, containerWidth),
    bounds.maxPx,
  );

  // A percentage resolves to 0 against an unmeasured container, which would
  // paint a collapsed panel for one frame. The declared length paints instead.
  const isPreMeasure = containerWidth === 0;
  const declaredWidthCss = toCssLength(
    request.controlledWidth ?? request.defaultWidth ?? 0,
  );
  const takenFromContent = isPreMeasure
    ? `${declaredWidthCss} - ${handleWidth}px`
    : `${panelWidth ? panelWidth + handleWidth : 0}px`;

  return {
    panel: {
      width: panelWidth,
      widthCss: isPreMeasure ? declaredWidthCss : `${panelWidth}px`,
      minPx: bounds.minPx,
      maxPx: bounds.maxPx,
    },
    content: {
      widthCss: `max(${toCssLength(
        constraints.reservedMin,
      )}, 100% - ${takenFromContent})`,
    },
  };
}
